import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import { array, number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { dailyForecastState, hasResetRefetchIntervalState } from '@store';

import { refetchIntervalByEndOfHour } from '../../helpers/refetchIntervalUtils';
import { toQueryString } from '../../helpers/StringUtils';
import { useGetUserLocation } from '../useGetUserLocation';

/**
 * Hourly Forecast endpoint returns weather for next 3, 5, 7, 10, or 15 days
 * If an invalid day is passed in, return an error
 */
const dailyForecastArgumentsSchema = object({
  lat: number().required(),
  lng: number().required(),
  language: string().default('en-US'),
  days: number().default(10),
});

const dailyForecastSchema = object({
  calendarDayTemperatureMax: array(number().nullable()),
  calendarDayTemperatureMin: array(number().nullable()),
  dayOfWeek: array(string().nullable()),
  expirationTimeUtc: array(number().nullable()),
  moonPhase: array(string().nullable()),
  moonPhaseCode: array(string().nullable()),
  moonPhaseDay: array(number().nullable()),
  moonriseTimeLocal: array(string().nullable()),
  moonriseTimeUtc: array(number().nullable()),
  moonsetTimeLocal: array(string().nullable()),
  moonsetTimeUtc: array(number().nullable()),
  narrative: array(string().nullable()),
  qpf: array(number().nullable()),
  qpfSnow: array(number().nullable()),
  sunriseTimeLocal: array(string().nullable()),
  sunriseTimeUtc: array(number().nullable()),
  sunsetTimeLocal: array(string().nullable()),
  sunsetTimeUtc: array(number().nullable()),
  temperatureMax: array(number().nullable()),
  temperatureMin: array(number().nullable()),
  validTimeLocal: array(string().nullable()),
  validTimeUtc: array(number().nullable()),
  daypart: array(
    object({
      cloudCover: array(number().nullable()),
      dayOrNight: array(string().nullable()),
      daypartName: array(string().nullable()),
      iconCode: array(number().nullable()),
      iconCodeExtend: array(number().nullable()),
      narrative: array(string().nullable()),
      precipChance: array(number().nullable()),
      precipType: array(string().nullable()),
      qpf: array(number().nullable()),
      qpfSnow: array(number().nullable()),
      qualifierCode: array(string().nullable()),
      qualifierPhrase: array(string().nullable()),
      relativeHumidity: array(number().nullable()),
      snowRange: array(string().nullable()),
      temperature: array(number().nullable()),
      temperatureHeatIndex: array(number().nullable()),
      temperatureWindChill: array(number().nullable()),
      thunderCategory: array(string().nullable()),
      thunderIndex: array(number().nullable()),
      uvDescription: array(string().nullable()),
      uvIndex: array(number().nullable()),
      windDirection: array(number().nullable()),
      windDirectionCardinal: array(string().nullable()),
      windPhrase: array(string().nullable()),
      windSpeed: array(number().nullable()),
      wxPhraseLong: array(string().nullable()),
      wxPhraseShort: array(string().nullable()),
    }),
  ),
});

const getApiUrl = ({ lat, lng, language, days }) =>
  [
    apiConfig.TWC_API_BASE,
    apiConfig.TWC_API_DAILY_FORECAST,
    `/${days}day`,
    toQueryString({
      language,
      apiKey: apiConfig.TWC_API_KEY,
      geocode: `${lat},${lng}`,
      units: 'e',
      format: 'json',
    }),
  ].join('');

const getDailyForecast = async parameters => {
  const validateArguments = await dailyForecastArgumentsSchema.validate(parameters);

  const url = getApiUrl(validateArguments);
  const { data } = await axios.get(url);

  return dailyForecastSchema.validate(data);
};

export const useGetDailyForecast = () => {
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const [state, setState] = useRecoilState(dailyForecastState);
  const [refetchInterval, setRefetchInterval] = useState(10 * MINUTE);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const { dailyWeather: dailyWeatherRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !dailyWeatherRefetched) {
      console.log('Refetching Daily Weather');
      refetchIntervalByEndOfHour({ setRefetchInterval });
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        dailyWeather: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  const { data, error, isLoading, isFetched } = useQuery(
    ['getDailyForecast', lat, lng],
    () => getDailyForecast({ lat, lng }),
    {
      enabled: Boolean(lat && lng),
      refetchOnWindowFocus: false,
      refetchInterval,
      retry: false,
      onSettled: (_data, _error) => {
        if (_data) {
          refetchIntervalByEndOfHour({ setRefetchInterval });
        } else {
          setRefetchInterval(2 * MINUTE);
        }
      },
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
